import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Package } from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import type { ProductSummary } from '@/shared/types'

interface Props {
  productId: number
  isMayParfum?: boolean
}

export function RelatedProducts({ productId, isMayParfum = false }: Props) {
  const lang = useAppStore((s) => s.lang)
  const T = useT('product')

  const { data: related } = useQuery<ProductSummary[]>({
    queryKey: ['related-products', productId],
    queryFn: () => api.get<ProductSummary[]>(`/products/${productId}/related`),
    staleTime: 5 * 60_000,
  })

  if (!related || related.length === 0) return null

  return (
    <section className="mt-6">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
        {T.relatedTitle}
      </h2>
      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
        {related.map((product) => {
          const name = localizedName(lang, product.name_uk, product.name_en)
          return (
            <Link
              key={product.id}
              to="/catalog/$productSlug"
              params={{ productSlug: product.slug || String(product.id) }}
              className={`w-32 shrink-0 snap-start overflow-hidden rounded-2xl border shadow-md transition-all hover:border-[#c9a24b] ${
                isMayParfum ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea]' : 'border-slate-800 bg-[#1e2329] text-slate-100'
              }`}
            >
              {product.photo_url ? (
                <img src={product.photo_url} alt={name} className="h-28 w-full object-cover" />
              ) : (
                <div className="flex h-28 items-center justify-center bg-[#1f242d]">
                  <Package className="h-6 w-6 text-slate-500" />
                </div>
              )}
              <div className="p-2.5">
                <p className="line-clamp-2 min-h-[32px] break-words text-xs font-medium leading-tight text-slate-200">{name}</p>
                <p className="mt-1 text-xs font-bold text-[#c9a24b]">
                  {formatPrice(product.price_uah)}
                </p>
              </div>
            </Link>
          )
        })}
      </div>
    </section>
  )
}
